import { SMB_ALL_QUARTERS, SMB_FY_QUARTER, SMB_QUARTERS } from '../configs'
import { IPlanningSmb, ISmbNode, TSmbExpandedMap, TSmbSplitStore } from '../model/types'

import { flattenSmbTree, getSmbNodeId } from './buildSmbTree'
import { getSmbEffectiveValue, TGetSmbWeight } from './getSmbEffectiveValue'
import { isSmbNodeExcluded } from './isSmbNodeExcluded'
import {
  getEuPercent,
  getSmbStoreKey,
  materializeQuarterFallbacks,
  normalizeSmbPercent,
  setStoredSplit,
} from './smbSplitStore'

export interface IPlanningSmbContext {
  partnerId: string
  programType: string
  /** The plan year and the two years after it, all planned in the same popup */
  years: readonly number[]
}

export interface IMapSmbStoreOptions {
  /** Financial Plan weights used to make the saved current-year FY agree with Q1..Q4. */
  getWeight: TGetSmbWeight
  /** Only quarterly mode replaces a loaded current-year FY with the weighted visible quarters. */
  derivePlanYearFyFromQuarters?: boolean
  /**
   * Branch expansion at the moment of saving. A row the user collapsed holds the split for
   * everything beneath it, and that is recorded so the next load knows the chosen grain.
   */
  expanded?: TSmbExpandedMap
}

const isAllowedQuarter = (year: number, fq: number, context: IPlanningSmbContext): boolean =>
  fq === SMB_FY_QUARTER ||
  (year === context.years[0] && SMB_QUARTERS.includes(fq as (typeof SMB_QUARTERS)[number]))

const cloneStore = (store: TSmbSplitStore): TSmbSplitStore => {
  const next: TSmbSplitStore = {}

  Object.entries(store).forEach(([key, entry]) => {
    if (entry) {
      const clonedEntry: NonNullable<TSmbSplitStore[string]> = {}
      Object.entries(entry).forEach(([fq, value]) => {
        if (value) {
          clonedEntry[Number(fq)] = { ...value }
        }
      })
      next[key] = clonedEntry
    }
  })

  return next
}

/**
 * Produces the authoritative picture sent to the procedure without mutating editor state.
 * Loaded data can contain both time grains, so a current-year node with resolvable quarters
 * always gets a fresh weighted FY. Pure display seeds are compacted when the same fallback is
 * still represented by an ancestor or by that node's FY value.
 */
export const canonicalizeSmbStoreForSave = (
  store: TSmbSplitStore,
  tree: ISmbNode,
  context: IPlanningSmbContext,
  getWeight: TGetSmbWeight,
  expanded?: TSmbExpandedMap,
  derivePlanYearFyFromQuarters = true
): TSmbSplitStore => {
  // A branch the user closed is authoritative at its own level, whatever its percentages came
  // from - that grain has to survive into the payload
  const isCollapsedBranch = (node: ISmbNode) =>
    !!expanded && node.children.length > 0 && !expanded[node.id]

  const next = cloneStore(store)
  const planYear = context.years[0]
  const nodes = flattenSmbTree(tree)

  // The two future years are yearly-only. Ignore malformed/stale quarter rows at a grain the
  // popup cannot show or edit, rather than silently sending them back to the procedure.
  nodes.forEach((node) => {
    context.years.slice(1).forEach((year) => {
      const key = getSmbStoreKey(node.id, year)
      const entry = next[key]
      if (!entry) {
        return
      }

      SMB_QUARTERS.forEach((fq) => delete entry[fq])
      if (!Object.values(entry).some(Boolean)) {
        delete next[key]
      }
    })
  })

  nodes.forEach((node) => {
    const entry = next[getSmbStoreKey(node.id, planYear)]
    if (!entry) {
      return
    }

    const hasGenuineQuarter = SMB_QUARTERS.some((fq) => {
      const value = entry[fq]

      return !!value && !value.isSeeded
    })
    const canResolveEveryOwnQuarter =
      !!entry[SMB_FY_QUARTER] || SMB_QUARTERS.every((fq) => !!entry[fq])
    const needsDerivedAnnual =
      !entry[SMB_FY_QUARTER] || (derivePlanYearFyFromQuarters && hasGenuineQuarter)
    if (!needsDerivedAnnual || !canResolveEveryOwnQuarter) {
      return
    }

    // A direct FY is the visible fallback for every missing quarter. Preserve those exact
    // quarter values before replacing FY with the weighted result; otherwise a later reload
    // would use the new average as the fallback and change the split a second time.
    materializeQuarterFallbacks(next, node.id, planYear)

    const annual = getSmbEffectiveValue(next, node, planYear, SMB_FY_QUARTER, getWeight, {
      deriveFyFromQuarters: true,
    })
    if (!annual.isEmpty) {
      setStoredSplit(next, node.id, planYear, SMB_FY_QUARTER, normalizeSmbPercent(annual.smb), true)
    }
  })

  const pruneDisplaySeeds = (node: ISmbNode, ancestorYears: ReadonlySet<number>): void => {
    const ownYears = new Set(ancestorYears)

    context.years.forEach((year) => {
      const key = getSmbStoreKey(node.id, year)
      const entry = next[key]
      if (!entry) {
        return
      }

      const values = SMB_ALL_QUARTERS.flatMap((fq) => (entry[fq] ? [entry[fq]!] : []))
      if (
        values.length &&
        values.every((value) => value.isSeeded) &&
        ancestorYears.has(year) &&
        !isCollapsedBranch(node)
      ) {
        delete next[key]

        return
      }

      SMB_ALL_QUARTERS.forEach((fq) => {
        const value = entry[fq]
        if (value) {
          value.smb = normalizeSmbPercent(value.smb)
        }
      })

      const annual = entry[SMB_FY_QUARTER]
      if (annual) {
        SMB_QUARTERS.forEach((fq) => {
          const quarter = entry[fq]
          if (
            quarter?.isSeeded &&
            normalizeSmbPercent(quarter.smb) === normalizeSmbPercent(annual.smb)
          ) {
            delete entry[fq]
          }
        })
      }

      if (Object.values(entry).some(Boolean)) {
        ownYears.add(year)
      } else {
        delete next[key]
      }
    })

    node.children.forEach((child) => pruneDisplaySeeds(child, ownYears))
  }

  pruneDisplaySeeds(tree, new Set())

  return next
}

/**
 * Stored split rows as they arrive from the back end, indexed by node and plan year.
 * Everything that came back was entered by a user, so nothing here counts as seeded.
 */
export const mapPlanningSmbToStore = (
  rows: IPlanningSmb[],
  context: IPlanningSmbContext,
  tree?: ISmbNode
): TSmbSplitStore => {
  const store: TSmbSplitStore = {}
  const allowedNodeIds = tree ? new Set(flattenSmbTree(tree).map((node) => node.id)) : null

  rows.forEach((row) => {
    const isSameScope =
      row.PartnerID === context.partnerId &&
      row.ProgramType === context.programType &&
      context.years.includes(row.FYear) &&
      SMB_ALL_QUARTERS.includes(row.FQuarter as (typeof SMB_ALL_QUARTERS)[number]) &&
      isAllowedQuarter(row.FYear, row.FQuarter, context) &&
      Number.isFinite(row.SMBPercent)
    if (!isSameScope) {
      return
    }

    const nodeId = getSmbNodeId(row.ProgramType, row.Category, row.SubCategory, row.L3Category)
    if (allowedNodeIds && !allowedNodeIds.has(nodeId)) {
      return
    }

    const key = getSmbStoreKey(nodeId, row.FYear)
    const entry = store[key] || (store[key] = {})
    entry[row.FQuarter] = {
      smb: normalizeSmbPercent(row.SMBPercent),
      isSeeded: false,
      ...(row.IsCollapsed === 1 ? { isPersistedCollapsed: true } : {}),
    }
  })

  return store
}

/**
 * The whole stored picture as rows for spJBPPlanningApplyChangesSMB, across every plan year.
 * Genuine values and any seeds that became necessary after a hierarchy override are saved.
 * Pure display-only seeds are omitted when an unchanged ancestor/FY remains authoritative.
 */
export const mapStoreToPlanningSmbRows = (
  store: TSmbSplitStore,
  tree: ISmbNode,
  context: IPlanningSmbContext,
  options?: IMapSmbStoreOptions
): IPlanningSmb[] => {
  const rows: IPlanningSmb[] = []
  const source = options
    ? canonicalizeSmbStoreForSave(
        store,
        tree,
        context,
        options.getWeight,
        options.expanded,
        options.derivePlanYearFyFromQuarters
      )
    : store
  // Without a known expansion state we make no claim about the grain
  const isCollapsedNode = (node: ISmbNode) =>
    !!options?.expanded && node.children.length > 0 && !options.expanded[node.id]
  const appendNode = (node: ISmbNode): void => {
    if (isSmbNodeExcluded(node)) {
      return
    }

    context.years.forEach((year) => {
      const entry = source[getSmbStoreKey(node.id, year)]
      if (!entry) {
        return
      }
      const isPersistedCollapsed = Object.values(entry).some((value) => value?.isPersistedCollapsed)

      SMB_ALL_QUARTERS.forEach((fq) => {
        if (!isAllowedQuarter(year, fq, context)) {
          return
        }

        const value = entry[fq]
        if (!value) {
          return
        }

        rows.push({
          PartnerID: context.partnerId,
          FYear: year,
          ProgramType: node.programType,
          Category: node.category,
          SubCategory: node.subCategory,
          L3Category: node.l3Category,
          FQuarter: fq,
          SMBPercent: value.smb,
          EUPercent: getEuPercent(value.smb),
          IsCollapsed: isCollapsedNode(node) || isPersistedCollapsed ? 1 : null,
        })
      })
    })
    node.children.forEach(appendNode)
  }

  appendNode(tree)

  return rows
}
